import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { Text } from '../../../../components/Text/Text';
import { Skeleton } from '../../../../components/Skeleton';
import { ANIM } from '../../../../theme/tokens';
import { RetryButton } from './LumioChat.styles';
import { useLumioConversations } from './useLumioConversations';
import {
  MENU_WIDTH,
  Surface,
  Heading,
  Scroll,
  List,
  Item,
  ItemTitle,
  ItemTime,
  StateRow,
  StateText,
  SkeletonRow,
} from './ConversationMenu.styles';

export interface ConversationMenuProps {
  open: boolean;
  /** The "…" trigger (the flyout is positioned from its viewport rect). */
  anchorRef: React.RefObject<HTMLElement>;
  onClose: () => void;
  /** A past conversation was picked: the parent resumes it and closes the menu. */
  onSelect: (conversationId: string) => void;
}

type Pos = { left: number; top: number; maxHeight: number };

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 8;
const SKELETON_ROWS = [0, 1, 2, 3];

/** Relative-time label ("just now" / "2 hours ago" / a date). Uses the same i18n
 * keys as the account activity list, so no new translations are introduced. */
const relativeTime = (t: TFunction, iso: string | null): string => {
  if (!iso) return '';
  // /chat/conversations returns ZONELESS UTC timestamps (e.g.
  // "2026-06-23T17:32:28.492000"); without a trailing Z, Date parses them as LOCAL
  // time, skewing every label by the viewer's UTC offset. Normalise to UTC.
  const normalized = iso.includes('T') && !/(?:Z|[+-]\d\d:?\d\d)$/.test(iso) ? `${iso}Z` : iso;
  const then = new Date(normalized).getTime();
  if (Number.isNaN(then)) return '';
  const sec = Math.round((Date.now() - then) / 1000);
  if (sec < 45) return t('just now');
  const min = Math.round(sec / 60);
  if (min < 60) return min <= 1 ? t('1 minute ago') : t('{{n}} minutes ago', { n: min });
  const hr = Math.round(min / 60);
  if (hr < 24) return hr <= 1 ? t('1 hour ago') : t('{{n}} hours ago', { n: hr });
  const day = Math.round(hr / 24);
  if (day < 30) return day <= 1 ? t('yesterday') : t('{{n}} days ago', { n: day });
  return new Date(normalized).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * The "…" past-conversations flyout. Portal-rendered and `fixed`-positioned from
 * the trigger's rect (opens downward, right-aligned), it lists the owner's recent
 * conversations with a first-message title + relative time. Self-contained:
 * fetches via `useLumioConversations`, handles outside-click / Escape / focus, and
 * shows honest loading / empty / error states (never fabricated rows).
 */
export const ConversationMenu = ({ open, anchorRef, onClose, onSelect }: ConversationMenuProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const { items, loading, error, reload } = useLumioConversations(open);

  // `mounted` keeps the node through the exit animation; `shown` drives the CSS state.
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const [pos, setPos] = useState<Pos | null>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const place = useCallback(() => {
    const r = anchorRef.current?.getBoundingClientRect();
    if (!r) return;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN));
    const top = r.bottom + ANCHOR_GAP;
    const maxHeight = Math.max(180, window.innerHeight - top - VIEWPORT_MARGIN);
    setPos({ left, top, maxHeight });
  }, [anchorRef]);

  useEffect(() => {
    if (open) {
      place();
      setMounted(true);
      const raf = requestAnimationFrame(() => {
        setShown(true);
        place();
      });
      return () => cancelAnimationFrame(raf);
    }
    setShown(false);
    const timer = window.setTimeout(() => setMounted(false), ANIM.MENU_CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [open, place]);

  // Keep pinned to the (fixed) trigger across viewport changes.
  useEffect(() => {
    if (!mounted) return undefined;
    const onChange = (): void => place();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [mounted, place]);

  // Outside-click + Escape close the menu (the trigger owns its own toggle click).
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (surfaceRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, anchorRef]);

  // Move focus into the menu once it has opened, and return it to whatever opened
  // the menu (the "…" trigger) on close, so keyboard/AT users keep their place.
  useEffect(() => {
    if (open) {
      openerRef.current = document.activeElement as HTMLElement | null;
    } else if (openerRef.current) {
      openerRef.current.focus?.();
      openerRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (shown) surfaceRef.current?.focus();
  }, [shown]);

  // Arrow-key navigation across the menu items (the WAI-ARIA menu keyboard model);
  // Tab still works too since the items are native buttons.
  const onKeyNav = useCallback((e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    const items = Array.from(surfaceRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    if (!items.length) return;
    e.preventDefault();
    const at = items.indexOf(document.activeElement as HTMLElement);
    let next = 0;
    if (e.key === 'End') next = items.length - 1;
    else if (e.key === 'ArrowDown') next = at < 0 ? 0 : Math.min(at + 1, items.length - 1);
    else if (e.key === 'ArrowUp') next = at < 0 ? items.length - 1 : Math.max(at - 1, 0);
    items[next]?.focus();
  }, []);

  if (!mounted) return null;

  return createPortal(
    <Surface
      ref={surfaceRef}
      role="menu"
      aria-label={t('Past conversations')}
      tabIndex={-1}
      onKeyDown={onKeyNav}
      $open={shown}
      style={pos ? { left: pos.left, top: pos.top, maxHeight: pos.maxHeight } : { visibility: 'hidden' }}
    >
      <Heading text="Past conversations" />
      <Scroll>
        {error ? (
          <StateRow>
            <StateText text="Couldn’t load conversations." />
            <RetryButton type="button" onClick={reload}>
              <Text text="Retry" />
            </RetryButton>
          </StateRow>
        ) : loading ? (
          <List>
            {SKELETON_ROWS.map((i) => (
              <SkeletonRow key={i}>
                <Skeleton width="80%" height={13} radius={4} block />
                <Skeleton width="40%" height={10} radius={4} block />
              </SkeletonRow>
            ))}
          </List>
        ) : items.length === 0 ? (
          <StateRow>
            <StateText text="No past conversations yet." />
          </StateRow>
        ) : (
          <List>
            {items.map((c, i) => (
              <Item key={c.id} type="button" role="menuitem" $index={i} $open={shown} onClick={() => onSelect(c.id)}>
                <ItemTitle text={c.title || t('Untitled conversation')} />
                {c.lastMessageAt ? <ItemTime>{relativeTime(t, c.lastMessageAt)}</ItemTime> : null}
              </Item>
            ))}
          </List>
        )}
      </Scroll>
    </Surface>,
    document.body,
  );
};

export default ConversationMenu;
